/**
 * Lo último que se vio de un proyecto, guardado en este navegador para pintar
 * el panel al instante la próxima vez.
 *
 * ── Para qué ────────────────────────────────────────────────────────────────
 * Las cachés del panel vivían en memoria: servían para cambiar de sección sin
 * esperar, pero al abrir el panel —lo que se hace cada mañana— todo empezaba de
 * cero. La pantalla de inicio tardaba un par de segundos en llenarse, pieza a
 * pieza, mientras llegaban el proyecto, sus páginas, las cifras y cada widget.
 *
 * Con esto se enseña lo de la última visita en el primer dibujo y se sustituye
 * por lo nuevo cuando llega, sin skeleton ni saltos. Es lo mismo que ya hacía
 * la lista de proyectos (`veltica:projects:` en `Home`).
 *
 * ── Qué se guarda y qué no ──────────────────────────────────────────────────
 * Sólo lo que es del dueño y pesa poco: el nombre del proyecto, sus fechas, la
 * lista de páginas SIN su contenido, las cifras agregadas, el blog, los
 * dominios y el uso. No los contactos, los mensajes ni las reservas: son datos
 * de terceros, y no se dejan en el disco de un navegador.
 *
 * Es sólo para ENSEÑAR. Nada de lo que sale de aquí se usa para guardar: lo
 * que edita y guarda el panel sigue esperando a la respuesta del servidor.
 */

/** Una semana: más vieja que eso engaña más de lo que ayuda. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Por entrada. Lo que pase de aquí no es «poco»: se queda sin guardar. */
const MAX_CHARS = 200_000
const PREFIX = 'veltica:snap:'

const memory = new Map<string, unknown>()
const keyFor = (projectId: string, name: string) => `${PREFIX}${projectId}:${name}`

/** Lo guardado, o `undefined`. `match`: una marca que tiene que coincidir (la consulta de unas cifras). */
export function readSnapshot<T>(projectId: string, name: string, match?: string): T | undefined {
  const key = keyFor(projectId, name) + (match ? `#${match}` : '')
  if (memory.has(key)) return memory.get(key) as T
  try {
    const raw = globalThis.localStorage.getItem(keyFor(projectId, name))
    if (!raw) return undefined
    const saved = JSON.parse(raw) as { at?: number; match?: string; value?: T }
    if (typeof saved.at !== 'number' || Date.now() - saved.at > MAX_AGE_MS) return undefined
    if ((saved.match ?? '') !== (match ?? '')) return undefined
    memory.set(key, saved.value)
    return saved.value
  } catch {
    return undefined
  }
}

/** Guarda. Una entrada por nombre: con `match` distinto, la nueva sustituye a la anterior. */
export function writeSnapshot<T>(projectId: string, name: string, value: T, match?: string): void {
  memory.set(keyFor(projectId, name) + (match ? `#${match}` : ''), value)
  try {
    const raw = JSON.stringify({ at: Date.now(), ...(match ? { match } : {}), value })
    if (raw.length > MAX_CHARS) return
    globalThis.localStorage.setItem(keyFor(projectId, name), raw)
  } catch {
    /* sin sitio o en privado: queda la copia en memoria */
  }
}

/** Al cerrar sesión: nada de esto se queda para quien use el navegador después. */
export function clearSnapshots(): void {
  memory.clear()
  try {
    const storage = globalThis.localStorage
    for (let index = storage.length - 1; index >= 0; index--) {
      const key = storage.key(index)
      if (key?.startsWith(PREFIX)) storage.removeItem(key)
    }
  } catch {
    /* sin almacenamiento no hay nada que borrar */
  }
}
